import { SUPABASE_URL, SUPABASE_ANON_KEY } from '@/config';
import { applyOrgFilter, getActiveOrgId } from '@/lib/activeOrg';
import { supabase } from '@/lib/supabase';

export type QbConnection = {
  id: string;
  realmId: string;
  companyName: string | null;
  status: string;
  lastSyncedAt: string | null;
  createdAt: string;
};

export type QbSyncResult = {
  synced: number;
  created: number;
  updated: number;
  linesCreated: number;
  linesMatched: number;
  vendorsCreated: number;
  transactions?: {
    created: number;
    updated: number;
    transfersDetected: number;
  };
};

/**
 * A QuickBooks connection that has stopped syncing.
 *
 * Staleness, not `status`, is the signal that matters. When the connection
 * silently died in July 2026 it kept reporting status='active' for 20 days
 * while nothing came through — the access token lives about an hour and the
 * refresh had stopped happening. Only `last_synced_at` revealed it.
 *
 * The nightly cron (fx-qb-sync-nightly, 08:15 UTC) means a healthy connection
 * syncs every day, so 48h allows one missed run before raising the alarm.
 */
export const QB_STALE_AFTER_HOURS = 48;

export function isQbConnectionStale(conn: QbConnection): boolean {
  if (conn.status !== 'active') return true;
  if (!conn.lastSyncedAt) return true;
  const hours = (Date.now() - new Date(conn.lastSyncedAt).getTime()) / 36e5;
  return hours > QB_STALE_AFTER_HOURS;
}

function mapConnection(row: any): QbConnection {
  return {
    id: row.id,
    realmId: row.realm_id,
    companyName: row.company_name,
    status: row.status,
    lastSyncedAt: row.last_synced_at,
    createdAt: row.created_at,
  };
}

async function invokeEdgeFunction(
  functionName: string,
  body: Record<string, unknown>
): Promise<any> {
  const {
    data: { session },
  } = await supabase.auth.getSession();

  // Without this the call goes out as `Bearer ` and comes back an opaque 401,
  // surfacing as "Failed to sync from QuickBooks" — which sends you looking at
  // the QuickBooks connection when the real problem is a signed-out session.
  // Sessions do get invalidated out from under the app (a project key/JWKS
  // rotation does it), and the UI still looks logged in from cached state.
  if (!session?.access_token) {
    throw new Error('Your session has expired. Please sign out and sign in again.');
  }

  const res = await fetch(`${SUPABASE_URL}/functions/v1/${functionName}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.access_token}`,
      apikey: SUPABASE_ANON_KEY,
    },
    body: JSON.stringify(body),
  });

  if (res.status === 401) {
    throw new Error('Your session has expired. Please sign out and sign in again.');
  }

  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Edge function error ${res.status}`);
  }

  return res.json();
}

const financeQuickBooksService = {
  async getAuthUrl(returnPath?: string): Promise<{ authUrl: string; state: string }> {
    return invokeEdgeFunction('fx-qb-auth-url', { organizationId: getActiveOrgId(), returnPath });
  },

  async getConnections(): Promise<QbConnection[]> {
    let query = supabase
      .from('fx_qb_connections')
      .select('*')
      .order('created_at', { ascending: false });

    query = applyOrgFilter(query);

    const { data, error } = await query;
    if (error) throw error;

    return (data ?? []).map(mapConnection);
  },

  async syncConnection(
    connectionId: string,
    options?: { startDate?: string; endDate?: string; syncType?: 'all' | 'transactions' | 'invoices' }
  ): Promise<QbSyncResult> {
    return invokeEdgeFunction('fx-qb-sync', {
      connectionId,
      startDate: options?.startDate,
      endDate: options?.endDate,
      syncType: options?.syncType ?? 'all',
    });
  },

  async deleteConnection(connectionId: string): Promise<void> {
    const { error } = await supabase.from('fx_qb_connections').delete().eq('id', connectionId);
    if (error) throw error;
  },
};

export default financeQuickBooksService;
